import {
    Alert,
    Chip,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import {
    ENVIRONMENTS,
    EnvironmentFilter,
    HIERARCHIES,
    Hierarchy,
    Period,
    environmentFilterSchema,
    hierarchySchema,
} from "@rossum/upcloud-costs-shared";

import { fetchCostTree, fetchPeriods } from "../../api/cost-tree-api";
import { AsyncContent } from "../../common/AsyncContent";
import { formatMonth } from "../../common/format";
import { useAsync } from "../../common/use-async";
import { CostTreeRow } from "./CostTreeRow";
import { MonthSelect } from "./MonthSelect";
import { defaultMonth } from "./default-month";

const ENVIRONMENT_FILTERS: readonly EnvironmentFilter[] = [...ENVIRONMENTS, "UNASSIGNED"];

/** Costs of a period as a tree organized by service and tenant. */
export function CostTreePage() {
    const { t } = useTranslation();
    const periods = useAsync(fetchPeriods, []);
    return (
        <Stack spacing={2}>
            <Typography variant="h5" component="h2">
                {t("costTree.title")}
            </Typography>
            <AsyncContent state={periods}>
                {({ periods: list }) =>
                    list.length === 0 ? (
                        <Alert severity="info">{t("costTree.noData")}</Alert>
                    ) : (
                        <CostTreeView periods={list} />
                    )
                }
            </AsyncContent>
        </Stack>
    );
}

function CostTreeView({ periods }: { periods: readonly Period[] }) {
    const { t, i18n } = useTranslation();
    const [params, setParams] = useSearchParams();
    const fallback = defaultMonth(periods) ?? "";
    const from = params.get("from") ?? fallback;
    const to = params.get("to") ?? from;
    const hierarchy: Hierarchy = hierarchySchema.catch("SERVICE_TENANT").parse(params.get("hierarchy"));
    const environment = params.getAll("environment").flatMap((value) => {
        const parsed = environmentFilterSchema.safeParse(value);
        return parsed.success ? [parsed.data] : [];
    });

    const update = (changes: Record<string, string | string[]>) => {
        const next = new URLSearchParams(params);
        for (const [key, value] of Object.entries(changes)) {
            next.delete(key);
            for (const item of typeof value === "string" ? [value] : value) {
                next.append(key, item);
            }
        }
        setParams(next);
    };

    const tree = useAsync(
        () => fetchCostTree({ from, to: to < from ? from : to, hierarchy, environment }),
        [from, to, hierarchy, environment.join(",")],
    );

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                <MonthSelect
                    label={t("costTree.from")}
                    value={from}
                    periods={periods}
                    onChange={(month) => {
                        update({ from: month, to: month > to ? month : to });
                    }}
                />
                <MonthSelect
                    label={t("costTree.to")}
                    value={to}
                    periods={periods}
                    onChange={(month) => {
                        update({ to: month, from: month < from ? month : from });
                    }}
                />
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={hierarchy}
                    aria-label={t("costTree.hierarchy")}
                    onChange={(_event, value: Hierarchy | null) => {
                        if (value !== null) {
                            update({ hierarchy: value });
                        }
                    }}>
                    {HIERARCHIES.map((value) => (
                        <ToggleButton key={value} value={value}>
                            {t(`hierarchy.${value}`)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Stack>
            <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{ flexWrap: "wrap" }}
                role="group"
                aria-label={t("costTree.environment")}>
                <Chip
                    label={t("costTree.allEnvironments")}
                    color={environment.length === 0 ? "primary" : "default"}
                    onClick={() => {
                        update({ environment: [] });
                    }}
                />
                {ENVIRONMENT_FILTERS.map((value) => {
                    const selected = environment.includes(value);
                    return (
                        <Chip
                            key={value}
                            label={t(`environment.${value}`)}
                            color={selected ? "primary" : "default"}
                            aria-pressed={selected}
                            onClick={() => {
                                update({
                                    environment: selected
                                        ? environment.filter((item) => item !== value)
                                        : [...environment, value],
                                });
                            }}
                        />
                    );
                })}
            </Stack>
            <AsyncContent state={tree}>
                {(response) => (
                    <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                            {t("costTree.comparison", {
                                from: formatMonth(response.previousFrom, i18n.language),
                                to: formatMonth(response.previousTo, i18n.language),
                            })}
                        </Typography>
                        <TableContainer component={Paper}>
                            <Table size="small" aria-label={t("costTree.title")}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("costTree.name")}</TableCell>
                                        <TableCell align="right">{t("costTree.amount")}</TableCell>
                                        <TableCell align="right">{t("costTree.previous")}</TableCell>
                                        <TableCell align="right">{t("costTree.change")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <CostTreeRow
                                        key={`${response.from}:${response.to}:${hierarchy}`}
                                        node={response.root}
                                        depth={0}
                                        currency={response.currency}
                                        initiallyExpanded
                                    />
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Stack>
                )}
            </AsyncContent>
        </Stack>
    );
}
